import { ANALYSES_TYPES, AnalysisKey } from '../../../../src/commonConstants';
import {
	LikedItem,
	PositioningGenerationContext,
	PositioningLayout,
	PERSONA_AGE_GROUPS,
	PERSONA_GENDERS,
	PersonaAgeGroup,
	PersonaGender,
	PositioningLane,
} from '../../../../src/commonTypes';
import { normalizePersonaGender } from '../../../../src/ai/personaImagePrompt';
import {
	ideateContextFoundationMessage,
	ideateContextModeLabel,
} from '../../../../src/services/board/ideatePositioning';
import {
	deletePositioningOutput,
	getUserLikes,
	setPositioningOutputLiked,
	shareLikeToTeam,
	updateSavedPersonaImage,
	updateUserPaletteLike,
	updateUserPersonaLike,
	updateUserTextLike,
	uploadBoardImage,
	userPersonaImage,
} from '../apiRequests';
import { closeAllPanels, showNonBlockingMessage } from './uiUtils';
import * as tutorialEvents from '../tutorials/tutorialEvents';
import { resolvePositioningCollisions } from './positioningCollision';
import { likedItemsToPositioningCards, PositioningCardData } from './positioningOutputAdapter';
import {
	positioningAddKey,
	positioningCardKey,
	positioningHubKey,
	positioningProblemKey,
} from './positioningMapKeys';

export type { PositioningLane } from '../../../../src/commonTypes';

export type IdeateWorkspaceController = {
	showOverview: () => void;
	showAnalysis: () => void;
	getProblemCenter: () => Point | null;
	refresh: () => Promise<void>;
	applyLayout: (layout: PositioningLayout) => void;
	destroy: () => void;
};

type IdeateWorkspaceOptions = {
	vision: string;
	insight?: string;
	pdfSummary?: string;
	pdfTopics?: string[];
	inspireLabels: string[];
	analyses: AnalysisKey[];
	isTeam: boolean;
	canConfigurePositioning: boolean;
	canvas: SVGGElement;
	onZoomToFit: () => void;
	onCenterMap: () => void;
	onAddOutput: (lane: PositioningLane) => void;
	onConfigureOutput: (
		context: PositioningGenerationContext,
		lane: PositioningLane,
		title: string
	) => void;
	onShowAnalysis: () => void;
	onShowOverview: () => void;
	initialLayout?: PositioningLayout;
	onLayoutChange?: (layout: PositioningLayout) => void;
};

type Point = { x: number; y: number };

const SVG_NS = 'http://www.w3.org/2000/svg';
const CARD_WIDTH = 380;
const CARD_HEIGHT = 360;
const PERSONA_CARD_WIDTH = 460;
const PERSONA_CARD_HEIGHT = 620;
const TEAM_OUTPUT_REFRESH_INTERVAL_MS = 3000;
const CARD_MENU_PAD_X = 24;
const CARD_MENU_PAD_TOP = 96;
const CARD_MENU_PAD_BOTTOM = 52;
const VIEW_CARD_FOUNDATIONS_EVENT = 'positioning-card-view-foundations';
const CONFIGURE_CARD_FOUNDATIONS_EVENT = 'positioning-card-configure-foundations';

const laneContent: Record<
	PositioningLane,
	{ eyebrow: string; title: string; hint: string; hub: Point }
> = {
	audience: {
		eyebrow: 'Who we are for',
		title: 'Audience & personas',
		hint: 'Generate audience profiles from the strongest psychographic signals.',
		hub: { x: -470, y: -150 },
	},
	motivation: {
		eyebrow: 'What moves them',
		title: 'Stakeholder motivations',
		hint: 'Reveal the needs and tensions that influence stakeholder behaviour.',
		hub: { x: 0, y: -480 },
	},
	solution: {
		eyebrow: 'How we create value',
		title: 'Solutions & opportunities',
		hint: 'Shape useful ideas around the problem and the people closest to it.',
		hub: { x: 470, y: -150 },
	},
	brand: {
		eyebrow: 'How we show up',
		title: 'Brand',
		hint: 'Define the values, voice, colours and signals that make the idea distinctive.',
		hub: { x: 290, y: 390 },
	},
	story: {
		eyebrow: 'How the story unfolds',
		title: 'Story & characters',
		hint: 'Shape narrative directions and the characters who bring the positioning to life.',
		hub: { x: -290, y: 390 },
	},
};

const firstSentence = (value: string): string => {
	const text = value.replace(/\s+/g, ' ').trim();
	if (!text) return '';
	const sentence = text.match(/^(.+?[.!?])(?:\s|$)/);
	return sentence?.[1] || text;
};

const plainText = (value: string): string => {
	const documentFragment = new DOMParser().parseFromString(value, 'text/html');
	return documentFragment.body.textContent?.replace(/\s+/g, ' ').trim() || '';
};

const insightDescriptionFor = (insight: string): string => {
	const labels = document.querySelectorAll<HTMLLabelElement>(
		'#analysesForm .first-question label[data-insight]'
	);
	for (const label of labels) {
		if (label.dataset.insight === insight) {
			return (label.dataset.description || '').trim();
		}
	}
	return '';
};

const summariseText = (value: string, max = 220): string => {
	const text = value.replace(/\s+/g, ' ').trim();
	if (!text) return '';
	if (text.length <= max) return text;
	return `${text.slice(0, max - 1).trimEnd()}…`;
};

const overviewCaptionFor = (card: PositioningCardData): string => {
	const titlePattern = card.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	const cleaned = plainText(card.summary || '')
		.replace(/!\[[^\]]*\]\([^)]*\)/g, '')
		.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
		.replace(/[*_~`>#]/g, '')
		.replace(new RegExp(`^${titlePattern}\\s*(?::|[-–—])?\\s*`, 'i'), '')
		.replace(/\s+/g, ' ')
		.trim();
	return summariseText(firstSentence(cleaned), 150);
};

const initials = (value: string): string =>
	value
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word) => word[0]?.toUpperCase())
		.join('');

const createElement = <K extends keyof HTMLElementTagNameMap>(
	tag: K,
	className?: string,
	text?: string
): HTMLElementTagNameMap[K] => {
	const element = document.createElement(tag);
	if (className) element.className = className;
	if (text !== undefined) element.innerText = text;
	return element;
};

const createContextItem = (label: string, value: string): HTMLElement => {
	const item = createElement('div', 'positioning-context-panel__item');
	item.append(createElement('span', undefined, label));
	item.append(createElement('p', undefined, value));
	return item;
};

const createContextChips = (label: string, values: string[], emptyText: string): HTMLElement => {
	const item = createElement('div', 'positioning-context-panel__item');
	item.append(createElement('span', undefined, label));
	if (!values.length) {
		item.append(createElement('p', undefined, emptyText));
		return item;
	}
	const list = createElement('ul', 'positioning-context-panel__chips');
	const visible = values.slice(0, 12);
	visible.forEach((value) => {
		list.append(createElement('li', undefined, value));
	});
	item.append(list);
	if (values.length > visible.length) {
		item.append(
			createElement(
				'p',
				'positioning-context-panel__more',
				`+${values.length - visible.length} more`
			)
		);
	}
	return item;
};

const createSvgElement = <K extends keyof SVGElementTagNameMap>(
	tag: K,
	className?: string
): SVGElementTagNameMap[K] => {
	const element = document.createElementNS(SVG_NS, tag);
	if (className) element.setAttribute('class', className);
	return element;
};

const escapeHtml = (value: string): string =>
	value.replace(/[&<>"']/g, (character) => {
		const entities: Record<string, string> = {
			'&': '&amp;',
			'<': '&lt;',
			'>': '&gt;',
			'"': '&quot;',
			"'": '&#39;',
		};
		return entities[character];
	});

const radialCardPosition = (
	lane: PositioningLane,
	index: number,
	width = CARD_WIDTH,
	height = CARD_HEIGHT
): Point => {
	const hub = laneContent[lane].hub;
	const hubDistance = Math.hypot(hub.x, hub.y) || 1;
	const outward = { x: hub.x / hubDistance, y: hub.y / hubDistance };
	const tangent = { x: -outward.y, y: outward.x };
	const slotOrder = [0, -1, 1, -2, 2];
	const slot = slotOrder[index % slotOrder.length];
	const ring = Math.floor(index / slotOrder.length);
	const radialDistance = 900 + ring * 390;
	const tangentSpacing = Math.max(400, width + 36);
	const center = {
		x: outward.x * radialDistance + tangent.x * slot * tangentSpacing,
		y: outward.y * radialDistance + tangent.y * slot * tangentSpacing,
	};
	return { x: center.x - width / 2, y: center.y - height / 2 };
};

const connectorPath = (lane: PositioningLane, from: Point, to: Point): string => {
	if (lane === 'audience' || lane === 'solution') {
		const midpoint = (from.x + to.x) / 2;
		return `M ${from.x} ${from.y} C ${midpoint} ${from.y}, ${midpoint} ${to.y}, ${to.x} ${to.y}`;
	}
	const midpoint = (from.y + to.y) / 2;
	return `M ${from.x} ${from.y} C ${from.x} ${midpoint}, ${to.x} ${midpoint}, ${to.x} ${to.y}`;
};

export const setupIdeateWorkspace = (
	options: IdeateWorkspaceOptions
): IdeateWorkspaceController | null => {
	const workspace = document.querySelector<HTMLElement>('#ideate-positioning-workspace');
	const analysesPanel = document.querySelector<HTMLElement>('#analyses-container');
	const returnButton = document.querySelector<HTMLButtonElement>('#ideate-positioning-return');
	const boardId = document.body.dataset.boardId;
	if (!workspace || !returnButton || !boardId) return null;

	const abortController = new AbortController();
	const { signal } = abortController;
	const sourceElements = Array.from(options.canvas.children);
	const mapRoot = createSvgElement('g', 'positioning-map-canvas');
	options.canvas.append(mapRoot);
	let destroyed = false;
	let currentCards: PositioningCardData[] = [];
	const positionState = new Map<string, Point>();
	Object.entries(options.initialLayout || {}).forEach(([key, point]) => {
		positionState.set(key, { x: point.x, y: point.y });
	});
	const nodeObjects = new Map<string, SVGForeignObjectElement>();
	let updateConnectors: () => void = () => {};
	let mapDragging = false;
	let applyingRemoteLayout = false;
	let lastPersistedLayout = JSON.stringify(options.initialLayout || {});
	const clearCardSelection = () => {
		mapRoot
			.querySelectorAll('.positioning-canvas-card-shell.is-selected')
			.forEach((selected) => selected.classList.remove('is-selected'));
	};
	const selectCardShell = (shell: Element) => {
		clearCardSelection();
		shell.classList.add('is-selected');
	};
	// Deselect when interacting with empty canvas / non-card map items (node-style selection).
	options.canvas.ownerSVGElement?.addEventListener(
		'pointerdown',
		(event) => {
			const target = event.target as Element | null;
			if (target?.closest?.('.positioning-canvas-card-shell')) return;
			clearCardSelection();
		},
		{ capture: true, signal }
	);
	const layoutFromState = (): PositioningLayout => {
		const layout: PositioningLayout = {};
		nodeObjects.forEach((_, key) => {
			const point = positionState.get(key);
			if (point) layout[key] = { x: point.x, y: point.y };
		});
		return layout;
	};
	const persistLayout = () => {
		if (applyingRemoteLayout || destroyed) return;
		const previous = (JSON.parse(lastPersistedLayout || '{}') || {}) as PositioningLayout;
		const layout = { ...previous, ...layoutFromState() };
		const serialized = JSON.stringify(layout);
		if (serialized === lastPersistedLayout) return;
		lastPersistedLayout = serialized;
		options.onLayoutChange?.(layout);
	};
	const applyLayout = (layout: PositioningLayout) => {
		if (mapDragging) return;
		applyingRemoteLayout = true;
		lastPersistedLayout = JSON.stringify(layout);
		Object.entries(layout).forEach(([key, point]) => {
			positionState.set(key, { x: point.x, y: point.y });
			const node = nodeObjects.get(key);
			if (!node) return;
			node.setAttribute('x', String(point.x));
			node.setAttribute('y', String(point.y));
		});
		updateConnectors();
		applyingRemoteLayout = false;
	};
	const resolveOutputCollisions = (
		anchoredKey?: string,
		iterations?: number,
		movableKeys?: Set<string>
	) => {
		const resolved = resolvePositioningCollisions(
			Array.from(nodeObjects.entries()).map(([key, node]) => ({
				key,
				x: Number(node.getAttribute('x')),
				y: Number(node.getAttribute('y')),
				width: Number(node.getAttribute('width')),
				height: Number(node.getAttribute('height')),
				movable:
					node.classList.contains('positioning-output-object') &&
					(!movableKeys || movableKeys.has(key)),
			})),
			// New output cards must find a genuinely clear slot instead of settling
			// after the default light collision pass beside a large persona card.
			{ anchoredKey, iterations: iterations || 72, padding: 40 }
		);
		resolved.forEach(({ key, x, y, movable }) => {
			if (!movable) return;
			const node = nodeObjects.get(key);
			if (!node) return;
			node.setAttribute('x', String(x));
			node.setAttribute('y', String(y));
			positionState.set(key, { x, y });
		});
	};
	const getNodeCenter = (key: string): Point | null => {
		const node = nodeObjects.get(key);
		if (!node) return null;
		return {
			x: Number(node.getAttribute('x')) + Number(node.getAttribute('width')) / 2,
			y: Number(node.getAttribute('y')) + Number(node.getAttribute('height')) / 2,
		};
	};

	const lensNames = options.analyses
		.map((analysisKey) => ANALYSES_TYPES[analysisKey]?.name)
		.filter(Boolean);

	const setSourceVisibility = (visible: boolean) => {
		sourceElements.forEach((element) => {
			element.classList.toggle('positioning-source-hidden', !visible);
		});
	};

	const showAnalysis = () => {
		options.onShowAnalysis();
		closeAllPanels();
		workspace.classList.add('hidden');
		mapRoot.classList.add('hidden');
		setSourceVisibility(true);
		analysesPanel?.classList.remove('hidden');
		returnButton.classList.remove('hidden');
		setTimeout(options.onZoomToFit, 0);
	};

	const createForeignObject = (
		key: string,
		position: Point,
		width: number,
		height: number,
		content: HTMLElement,
		className?: string
	): SVGForeignObjectElement => {
		const savedPosition = positionState.get(key) || position;
		positionState.set(key, savedPosition);
		const foreignObject = createSvgElement('foreignObject', className);
		foreignObject.dataset.mapKey = key;
		foreignObject.setAttribute('x', String(savedPosition.x));
		foreignObject.setAttribute('y', String(savedPosition.y));
		foreignObject.setAttribute('width', String(width));
		foreignObject.setAttribute('height', String(height));
		content.classList.add('positioning-draggable');
		const isOutputCardShell = content.classList.contains('positioning-canvas-card-shell');
		content.addEventListener(
			'pointerdown',
			(event) => {
				const interactive = (event.target as HTMLElement).closest(
					'button, a, label, summary, input, select, textarea, [role="button"], [contenteditable="true"], [contenteditable="plaintext-only"]'
				);
				// Ignore nested controls, but not the card shell itself (it is role=button).
				if (interactive && interactive !== content) {
					event.stopPropagation();
					return;
				}
				event.preventDefault();
				event.stopPropagation();
				content.setPointerCapture(event.pointerId);
				mapDragging = true;
				const startPointer = { x: event.clientX, y: event.clientY };
				const startPosition = {
					x: Number(foreignObject.getAttribute('x')),
					y: Number(foreignObject.getAttribute('y')),
				};
				let hasMoved = false;
				const matrix = options.canvas.getScreenCTM();
				const scale = matrix ? Math.max(0.1, Math.hypot(matrix.a, matrix.b)) : 1;
				content.onpointermove = (moveEvent) => {
					const movedFarEnough =
						Math.hypot(moveEvent.clientX - startPointer.x, moveEvent.clientY - startPointer.y) > 4;
					if (movedFarEnough && !hasMoved) {
						hasMoved = true;
						content.classList.add('is-dragging');
					}
					if (!hasMoved) return;
					const nextPosition = {
						x: startPosition.x + (moveEvent.clientX - startPointer.x) / scale,
						y: startPosition.y + (moveEvent.clientY - startPointer.y) / scale,
					};
					foreignObject.setAttribute('x', String(nextPosition.x));
					foreignObject.setAttribute('y', String(nextPosition.y));
					positionState.set(key, nextPosition);
					resolveOutputCollisions(key, 6);
					updateConnectors();
				};
				const finishDrag = () => {
					if (hasMoved) {
						content.addEventListener(
							'click',
							(clickEvent) => {
								clickEvent.preventDefault();
								clickEvent.stopImmediatePropagation();
							},
							{ capture: true, once: true, signal }
						);
					} else if (isOutputCardShell) {
						// Pointer capture retargets click to the shell, so activate here
						// (same click→menu-open model as Inspire/Synthesise nodes).
						content.dispatchEvent(new Event('positioning-card-select'));
					}
					content.classList.remove('is-dragging');
					content.onpointermove = null;
					content.onpointerup = null;
					content.onpointercancel = null;
					mapDragging = false;
					if (hasMoved) persistLayout();
				};
				content.onpointerup = finishDrag;
				content.onpointercancel = finishDrag;
			},
			{ signal }
		);
		content.addEventListener('click', (event) => event.stopPropagation(), { signal });
		foreignObject.append(content);
		nodeObjects.set(key, foreignObject);
		return foreignObject;
	};

	const createProblemNode = () => {
		const node = createElement('article', 'positioning-problem-node');
		node.append(createElement('p', undefined, 'THE PROBLEM'));
		node.append(createElement('h3', undefined, 'What are we solving?'));
		node.append(
			createElement(
				'p',
				'positioning-problem-node__copy',
				options.vision.trim() ||
					'Clarify the customer problem that every audience, stakeholder and solution should address.'
			)
		);
		return createForeignObject(
			positioningProblemKey,
			{ x: -175, y: -175 },
			350,
			350,
			node,
			'positioning-problem-object'
		);
	};

	const createBranchNode = (lane: PositioningLane) => {
		const content = laneContent[lane];
		const node = createElement(
			'article',
			`positioning-branch-node positioning-branch-node--${lane}`
		);
		node.append(createElement('p', undefined, content.eyebrow));
		node.append(createElement('h3', undefined, content.title));
		node.append(createElement('p', 'positioning-branch-node__caption', content.hint));
		return createForeignObject(
			positioningHubKey(lane),
			{ x: content.hub.x - 125, y: content.hub.y - 73 },
			250,
			146,
			node,
			'positioning-branch-object'
		);
	};

	const createOutputCard = (card: PositioningCardData) => {
		const cardAnalysisNames = card.positioningContext?.sources
			.map((source) => source.analysisName)
			.filter((name, index, names) => names.indexOf(name) === index);
		const sourceNames = cardAnalysisNames?.length ? cardAnalysisNames : lensNames;
		const isPersona = Boolean(card.persona);
		const article = createElement(
			'article',
			`positioning-canvas-card positioning-canvas-card--${card.lane}${
				isPersona ? ' positioning-canvas-card--persona' : ''
			}`
		);
		article.dataset.positioningOutputId = card.itemId || '';
		article.dataset.positioningOutputClientId = card.clientId || '';
		article.dataset.positioningOutputTitle = card.title;
		article.dataset.hasPositioningContext = String(Boolean(card.positioningContext));
		if (!card.ownedByCurrentUser) {
			article.classList.add('positioning-canvas-card--not-owned');
			const ownerColour = card.ownerColour || '';
			if (/^#[0-9a-f]{3,8}$/i.test(ownerColour)) {
				article.style.setProperty('--positioning-card-owner-colour', ownerColour);
			} else if (/^[a-z][a-z0-9-]*$/i.test(ownerColour)) {
				article.style.setProperty('--positioning-card-owner-colour', `var(--${ownerColour})`);
			}
		}
		const inner = createElement('div', 'positioning-canvas-card__inner');
		const front = createElement(
			'div',
			'positioning-canvas-card__face positioning-canvas-card__front'
		);
		const header = createElement('div', 'positioning-canvas-card__header');
		const saveStatus = createElement('span', 'positioning-canvas-card__save-status');
		const likeControl = createElement('div', 'positioning-canvas-card__like-control');
		const likeButton = createElement('button', 'positioning-canvas-card__like');
		likeButton.type = 'button';
		const likeIcon = createElement('img');
		likeIcon.alt = '';
		likeIcon.setAttribute('aria-hidden', 'true');
		likeButton.append(likeIcon);
		const likeCount = createElement('button', 'positioning-canvas-card__like-count');
		likeCount.type = 'button';
		const likePopover = createElement('div', 'positioning-canvas-card__like-popover');
		likePopover.setAttribute('role', 'status');
		likeControl.append(likeButton, likeCount, likePopover);
		const renderLikers = () => {
			likePopover.replaceChildren();
			likePopover.append(
				createElement(
					'strong',
					undefined,
					card.likeCount === 1 ? 'Liked by 1 person' : `Liked by ${card.likeCount} people`
				)
			);
			if (card.likedByUsers.length === 0) {
				likePopover.append(createElement('span', undefined, 'No likes yet'));
				return;
			}
			card.likedByUsers.forEach((user) => {
				const row = createElement('span', 'positioning-canvas-card__liker');
				const dot = createElement('i');
				if (/^#[0-9a-f]{3,8}$/i.test(user.colour)) {
					dot.style.backgroundColor = user.colour;
				} else if (/^[a-z][a-z0-9-]*$/i.test(user.colour)) {
					dot.style.backgroundColor = `var(--${user.colour})`;
				}
				row.append(dot, document.createTextNode(user.name));
				likePopover.append(row);
			});
		};
		const syncLikeButton = () => {
			likeButton.classList.toggle('is-liked', card.likedByCurrentUser);
			likeButton.setAttribute('aria-pressed', String(card.likedByCurrentUser));
			likeButton.setAttribute(
				'aria-label',
				`${card.likedByCurrentUser ? 'Unlike' : 'Like'} ${card.title}`
			);
			likeButton.title = card.likedByCurrentUser ? 'Unlike this output' : 'Like this output';
			likeIcon.src = `/assets/icon/board/heart-${card.likedByCurrentUser ? 'full' : 'empty'}.png`;
			likeCount.innerText = String(card.likeCount);
			likeCount.setAttribute(
				'aria-label',
				`Show who liked ${card.title}; ${card.likeCount} total likes`
			);
			renderLikers();
		};
		const setLikeState = (
			liked: boolean,
			count: number,
			likedByUsers: NonNullable<LikedItem['likedByUsers']> = card.likedByUsers
		) => {
			card.likedByCurrentUser = liked;
			card.likeCount = Math.max(0, count);
			card.likedByUsers = likedByUsers;
			syncLikeButton();
		};
		likeButton.disabled = !card.itemId;
		syncLikeButton();
		likeControl.addEventListener('pointerdown', (event) => event.stopPropagation(), { signal });
		likeControl.addEventListener('mousedown', (event) => event.stopPropagation(), { signal });
		likeCount.addEventListener(
			'click',
			(event) => {
				event.preventDefault();
				event.stopPropagation();
				likeControl.classList.toggle('is-open');
			},
			{ signal }
		);
		likeButton.addEventListener(
			'click',
			(event) => {
				event.preventDefault();
				event.stopPropagation();
				if (!card.itemId || likeButton.dataset.pending === 'true') return;
				const previousLiked = card.likedByCurrentUser;
				const previousCount = card.likeCount;
				const nextLiked = !previousLiked;
				setLikeState(nextLiked, previousCount + (nextLiked ? 1 : -1));
				likeButton.dataset.pending = 'true';
				likeButton.disabled = true;
				void setPositioningOutputLiked(boardId, card.itemId, nextLiked)
					.then((result) => {
						setLikeState(result.liked, result.likeCount, result.likedByUsers);
						window.dispatchEvent(
							new CustomEvent('positioning-output-like-changed', {
								detail: { itemId: card.itemId, ...result },
							})
						);
					})
					.catch((error) => {
						console.error('Unable to like positioning output', error);
						setLikeState(previousLiked, previousCount);
						showNonBlockingMessage('Unable to update this like. Please try again.');
					})
					.finally(() => {
						delete likeButton.dataset.pending;
						likeButton.disabled = false;
					});
			},
			{ signal }
		);
		window.addEventListener(
			'positioning-output-like-changed',
			(event) => {
				const detail = (
					event as CustomEvent<{
						itemId?: string;
						liked: boolean;
						likeCount: number;
						likedByUsers: NonNullable<LikedItem['likedByUsers']>;
					}>
				).detail;
				if (detail.itemId === card.itemId) {
					setLikeState(detail.liked, detail.likeCount, detail.likedByUsers);
				}
			},
			{ signal }
		);
		let saveQueue = Promise.resolve();
		const persistPersona = () => {
			if (!card.persona || !card.itemId || !card.editable) return;
			saveStatus.innerText = 'Saving…';
			saveStatus.classList.remove('is-error');
			saveQueue = saveQueue
				.catch(() => undefined)
				.then(async () => {
					await updateUserPersonaLike(boardId, card.itemId!, card.persona!);
					saveStatus.innerText = 'Saved';
				})
				.catch((error) => {
					console.error('Unable to update persona card', error);
					saveStatus.innerText = 'Could not save';
					saveStatus.classList.add('is-error');
				});
		};
		const persistPalette = () => {
			if (!card.palette || !card.itemId || !card.editable) return;
			saveStatus.innerText = 'Saving…';
			saveStatus.classList.remove('is-error');
			saveQueue = saveQueue
				.catch(() => undefined)
				.then(async () => {
					await updateUserPaletteLike(boardId, card.itemId!, card.palette!);
					saveStatus.innerText = 'Saved';
				})
				.catch((error) => {
					console.error('Unable to update brand palette card', error);
					saveStatus.innerText = 'Could not save';
					saveStatus.classList.add('is-error');
				});
		};
		const persistText = () => {
			if (!card.itemId || !card.editable || !card.ownedByCurrentUser) return;
			const text = card.motivations
				? JSON.stringify(card.motivations)
				: card.rawText || card.summary;
			saveStatus.innerText = 'Saving…';
			saveStatus.classList.remove('is-error');
			saveQueue = saveQueue
				.catch(() => undefined)
				.then(async () => {
					await updateUserTextLike(boardId, card.itemId!, text, card.title);
					saveStatus.innerText = 'Saved';
				})
				.catch((error) => {
					console.error('Unable to update output card', error);
					saveStatus.innerText = 'Could not save';
					saveStatus.classList.add('is-error');
				});
		};
		const makeEditable = (
			element: HTMLElement,
			label: string,
			getValue: () => string,
			setValue: (value: string) => string | void,
			singleLine = false,
			persist: () => void = persistPersona
		) => {
			if (!card.editable) return;
			// `plaintext-only` can receive focus but reject keyboard input inside an
			// SVG foreignObject in some browsers. Use the interoperable editable mode;
			// blur handling below still reads and persists plain text only.
			element.contentEditable = 'true';
			element.classList.add('positioning-canvas-card__editable');
			element.setAttribute('role', 'textbox');
			element.setAttribute('aria-label', `Edit ${label}`);
			element.spellcheck = true;
			// D3 zoom listens for `mousedown` on the ancestor SVG. Stopping only
			// `pointerdown` in the draggable card still lets that compatibility event
			// reach D3, which prevents the browser from placing an editable caret.
			const keepEditingGestureInsideCard = (event: Event) => event.stopPropagation();
			element.addEventListener('pointerdown', keepEditingGestureInsideCard, { signal });
			element.addEventListener('mousedown', keepEditingGestureInsideCard, { signal });
			element.addEventListener('touchstart', keepEditingGestureInsideCard, { signal });
			element.addEventListener('click', keepEditingGestureInsideCard, { signal });
			element.addEventListener('dblclick', keepEditingGestureInsideCard, { signal });
			let valueBeforeEdit = getValue();
			element.addEventListener('focus', () => {
				valueBeforeEdit = getValue();
			});
			element.addEventListener('keydown', (event) => {
				event.stopPropagation();
				if (event.key === 'Escape') {
					event.preventDefault();
					element.innerText = valueBeforeEdit;
					element.blur();
				} else if (singleLine && event.key === 'Enter') {
					event.preventDefault();
					element.blur();
				}
			});
			element.addEventListener('blur', () => {
				const nextValue = element.innerText.replace(/\s+/g, ' ').trim();
				if (!nextValue) {
					element.innerText = valueBeforeEdit;
					return;
				}
				if (nextValue === getValue()) return;
				const persistedValue = setValue(nextValue) || nextValue;
				element.innerText = persistedValue;
				const overviewCaption = article.querySelector<HTMLElement>(
					'.positioning-canvas-card__overview-caption'
				);
				if (overviewCaption) overviewCaption.innerText = overviewCaptionFor(card);
				persist();
			});
		};

		const portrait = createElement('div', 'positioning-canvas-card__portrait');
		const renderPortrait = (imageUrl?: string) => {
			// Image controls live on the portrait itself. Preserve them when a
			// generated/uploaded image replaces the initials placeholder.
			const imageActions = portrait.querySelector<HTMLElement>(
				'.positioning-canvas-card__image-actions'
			);
			portrait.replaceChildren();
			if (imageUrl) {
				const image = createElement('img');
				image.src = imageUrl;
				image.alt = `${card.title} portrait`;
				image.loading = 'lazy';
				portrait.append(image);
			} else {
				portrait.append(createElement('span', undefined, initials(card.title) || '•'));
			}
			if (imageActions) portrait.append(imageActions);
		};
		renderPortrait(card.imageUrl);
		header.append(portrait);

		const heading = createElement('div');
		heading.append(createElement('p', 'positioning-canvas-card__eyebrow', card.eyebrow));
		const frontTitle = createElement('h4', undefined, card.title);
		heading.append(frontTitle);
		if (card.subtitle) heading.append(createElement('small', undefined, card.subtitle));
		if (card.editable) heading.append(saveStatus);
		header.append(heading, likeControl);
		front.append(header);
		const summaryElement = createElement('p', 'positioning-canvas-card__summary', card.summary);
		const overviewCaption = createElement(
			'p',
			'positioning-canvas-card__overview-caption',
			overviewCaptionFor(card)
		);
		front.append(summaryElement, overviewCaption);
		if (card.palette) {
			makeEditable(
				frontTitle,
				'brand palette name',
				() => card.palette!.name || card.title,
				(value) => {
					card.palette!.name = value;
					card.title = value;
				},
				true,
				persistPalette
			);
			makeEditable(
				summaryElement,
				'brand palette rationale',
				() => card.palette!.rationale || '',
				(value) => {
					card.palette!.rationale = value;
					card.summary = value;
				},
				false,
				persistPalette
			);
		} else if (card.motivations && card.motivationIndex !== undefined) {
			const stakeholder = card.motivations.stakeholders[card.motivationIndex];
			if (stakeholder) {
				makeEditable(
					frontTitle,
					'stakeholder name',
					() => stakeholder.name,
					(value) => {
						stakeholder.name = value;
						card.title = value;
					},
					true,
					persistText
				);
				makeEditable(
					summaryElement,
					'stakeholder motivation',
					() => stakeholder.description,
					(value) => {
						stakeholder.description = value;
						card.summary = value;
					},
					false,
					persistText
				);
			}
		} else if (card.rawText !== undefined) {
			makeEditable(
				frontTitle,
				'output title',
				() => card.title,
				(value) => {
					card.title = value;
				},
				true,
				persistText
			);
			makeEditable(
				summaryElement,
				'output text',
				() => card.rawText || '',
				(value) => {
					card.rawText = value;
					card.summary = value;
					if (card.details[0]) card.details[0].value = value;
				},
				false,
				persistText
			);
		}

		if (card.persona) {
			makeEditable(
				frontTitle,
				`${card.eyebrow.toLowerCase()} name`,
				() => card.persona!.name,
				(value) => {
					card.persona!.name = value;
					card.title = value;
					renderPortrait(card.imageUrl);
				},
				true
			);
		}

		if (isPersona) {
			const highlights = createElement('div', 'positioning-canvas-card__profile');
			card.details
				.filter((detail) => detail.value)
				.forEach((detail) => {
					const section = createElement('section');
					section.append(createElement('h5', undefined, detail.title));
					const value = createElement('p', undefined, detail.value);
					if (card.persona && detail.field) {
						makeEditable(
							value,
							detail.title,
							() => card.persona![detail.field!] || '',
							(nextValue) => {
								if (detail.field === 'ageGroup') {
									const ageGroup = PERSONA_AGE_GROUPS.includes(nextValue as PersonaAgeGroup)
										? (nextValue as PersonaAgeGroup)
										: card.persona!.ageGroup || '25–34';
									card.persona!.ageGroup = ageGroup;
									detail.value = ageGroup;
									return ageGroup;
								}
								if (detail.field === 'gender') {
									const gender =
										normalizePersonaGender(nextValue) ||
										(PERSONA_GENDERS.includes(nextValue as PersonaGender)
											? (nextValue as PersonaGender)
											: card.persona!.gender);
									if (gender) {
										card.persona!.gender = gender;
										detail.value = gender;
										return gender;
									}
									delete card.persona!.gender;
									detail.value = '';
									return '';
								}
								card.persona![detail.field!] = nextValue;
								detail.value = nextValue;
								if (detail.field === 'biography') card.summary = firstSentence(nextValue);
							},
							detail.field === 'ageGroup' || detail.field === 'gender'
						);
					}
					section.append(value);
					highlights.append(section);
				});
			front.append(highlights);

			if (card.editable && card.persona && card.itemId) {
				const imageActions = createElement('div', 'positioning-canvas-card__image-actions');
				imageActions.addEventListener('pointerdown', (event) => event.stopPropagation());
				const generateImage = createElement('button', undefined, '✦');
				generateImage.type = 'button';
				generateImage.title = 'Generate image';
				generateImage.setAttribute('aria-label', 'Generate image');
				const uploadButton = createElement('button', undefined, '↥');
				uploadButton.type = 'button';
				uploadButton.title = 'Upload image';
				uploadButton.setAttribute('aria-label', 'Upload image');
				const uploadInput = createElement('input');
				uploadInput.type = 'file';
				uploadInput.accept = 'image/png,image/jpeg,image/webp';
				uploadInput.hidden = true;
				const removeImage = createElement('button', undefined, '×');
				removeImage.type = 'button';
				removeImage.title = 'Remove image';
				removeImage.setAttribute('aria-label', 'Remove image');
				removeImage.hidden = !card.imageUrl;
				const applyImage = async (imageUrl: string) => {
					card.imageUrl = imageUrl;
					if (imageUrl) card.persona!.imageUrl = imageUrl;
					else delete card.persona!.imageUrl;
					renderPortrait(imageUrl);
					removeImage.hidden = !imageUrl;
					await updateUserPersonaLike(boardId, card.itemId!, card.persona!);
					if (card.clientId) await updateSavedPersonaImage(boardId, card.clientId, imageUrl);
				};
				generateImage.addEventListener(
					'click',
					() =>
						void (async () => {
							generateImage.disabled = true;
							generateImage.innerText = 'Generating…';
							try {
								const result = await userPersonaImage(boardId, card.persona!);
								await applyImage(result.snippet);
							} catch (error) {
								console.error('Unable to generate persona image', error);
							} finally {
								generateImage.disabled = false;
								generateImage.innerText = '✦';
							}
						})()
				);
				uploadButton.addEventListener('click', (event) => {
					event.preventDefault();
					event.stopPropagation();
					uploadInput.click();
				});
				uploadInput.addEventListener(
					'change',
					() =>
						void (async () => {
							const file = uploadInput.files?.[0];
							if (!file) return;
							try {
								await applyImage(await uploadBoardImage(boardId, file));
							} catch (error) {
								console.error('Unable to upload persona image', error);
							} finally {
								uploadInput.value = '';
							}
						})()
				);
				removeImage.addEventListener('click', (event) => {
					event.preventDefault();
					event.stopPropagation();
					void applyImage('').catch((error) =>
						console.error('Unable to remove persona image', error)
					);
				});
				imageActions.append(generateImage, uploadButton, removeImage, uploadInput);
				portrait.append(imageActions);
			}
		} else if (card.palette) {
			const palette = createElement('div', 'positioning-canvas-card__palette');
			palette.addEventListener('pointerdown', (event) => event.stopPropagation());
			const syncPaletteDetails = () => {
				card.details = card.palette!.colours.map((colour) => ({
					title: `${colour.role}: ${colour.name}`,
					value: colour.hex,
				}));
			};
			const renderPalette = () => {
				palette.replaceChildren();
				palette.style.gridTemplateColumns = `repeat(${card.palette!.colours.length}, 1fr)`;
				card.palette!.colours.forEach((colour, index) => {
					const item = createElement('div', 'positioning-canvas-card__palette-colour');
					const swatch = createElement('input', 'positioning-canvas-card__colour-input');
					swatch.type = 'color';
					swatch.value = /^#[0-9a-f]{6}$/i.test(colour.hex) ? colour.hex : '#000000';
					swatch.title = `${colour.name} · ${colour.hex} · ${colour.role}`;
					swatch.setAttribute('aria-label', `Edit ${colour.name} colour`);
					swatch.disabled = !card.editable;
					swatch.addEventListener('input', () => {
						colour.hex = swatch.value.toUpperCase();
						swatch.title = `${colour.name} · ${colour.hex} · ${colour.role}`;
						syncPaletteDetails();
					});
					swatch.addEventListener('change', persistPalette);
					item.append(swatch);
					if (card.editable && card.palette!.colours.length > 1) {
						const remove = createElement('button', 'positioning-canvas-card__colour-remove', '×');
						remove.type = 'button';
						remove.title = `Remove ${colour.name}`;
						remove.setAttribute('aria-label', `Remove ${colour.name} from palette`);
						remove.addEventListener('click', (event) => {
							event.preventDefault();
							event.stopPropagation();
							card.palette!.colours.splice(index, 1);
							syncPaletteDetails();
							renderPalette();
							persistPalette();
						});
						item.append(remove);
					}
					palette.append(item);
				});
			};
			renderPalette();
			front.append(palette);
		}

		const sources = createElement('div', 'positioning-canvas-card__sources');
		(sourceNames.length ? sourceNames.slice(0, 3) : ['Inspire signals']).forEach((lens) => {
			sources.append(createElement('span', undefined, lens));
		});
		if (!isPersona) front.append(sources);

		// Keep the evidence that produced an output on the card itself.  This is a
		// snapshot, deliberately independent from the current Analyses Explorer
		// selection, so a later change of filters cannot rewrite its provenance.
		const back = createElement(
			'div',
			'positioning-canvas-card__face positioning-canvas-card__back'
		);
		const backHeader = createElement('div', 'positioning-canvas-card__back-header');
		backHeader.append(createElement('p', 'positioning-canvas-card__eyebrow', 'Psychographic foundations'));
		backHeader.append(createElement('h4', undefined, card.title));
		back.append(backHeader);
		const foundations = createElement('div', 'positioning-canvas-card__foundation-grid');
		const foundationDetails = card.details.filter((detail) => detail.value).slice(0, 8);
		if (foundationDetails.length) {
			foundationDetails.forEach((detail) => {
				const section = createElement('section');
				section.append(createElement('h5', undefined, detail.title));
				section.append(createElement('p', undefined, detail.value));
				foundations.append(section);
			});
		} else {
			foundations.append(createElement('p', 'positioning-canvas-card__empty-foundations', 'This output has no structured foundations saved yet.'));
		}
		back.append(foundations);
		const recipe = createElement('section', 'positioning-canvas-card__analysis-recipe');
		recipe.append(createElement('h5', undefined, 'Analysis recipe used'));
		const recipeChips = createElement('div', 'positioning-canvas-card__recipe-chips');
		const recipeLabels = [
			...(card.positioningContext?.sources || []).map((source) =>
				[source.analysisName, source.cluster].filter(Boolean).join(': ')
			),
			...(card.positioningContext?.socialPerspectiveBlueprint
				? [
					`Social: ${card.positioningContext.socialPerspectiveBlueprint.primaryStance}`,
					...card.positioningContext.socialPerspectiveBlueprint.emotions.map(
						(emotion) => `Emotion: ${emotion}`
					),
				]
				: []),
		];
		(recipeLabels.length ? recipeLabels.slice(0, 6) : ['No saved analysis configuration']).forEach((label) =>
			recipeChips.append(createElement('span', undefined, label))
		);
		recipe.append(recipeChips);
		back.append(recipe);

		const radialMenu = createElement('div', 'positioning-canvas-card__actions');
		const foundationsButton = createElement(
			'button',
			'positioning-canvas-card__action positioning-canvas-card__action--foundations',
			'👁 Foundations'
		);
		foundationsButton.type = 'button';
		foundationsButton.hidden = !card.positioningContext;
		const shareButton = createElement(
			'button',
			'positioning-canvas-card__action positioning-canvas-card__action--share',
			'↗ Share'
		);
		shareButton.type = 'button';
		shareButton.title = 'Share with Team';
		shareButton.setAttribute('aria-label', 'Share with Team');
		const deleteButton = createElement(
			'button',
			'positioning-canvas-card__action positioning-canvas-card__action--delete',
			'🗑 Delete output'
		);
		deleteButton.type = 'button';
		deleteButton.hidden = !card.itemId || !card.ownedByCurrentUser;
		const recipeButton = createElement(
			'button',
			'positioning-canvas-card__action positioning-canvas-card__action--recipe',
			'⚙ Recipe'
		);
		recipeButton.type = 'button';
		recipeButton.title = 'Configure recipe';
		recipeButton.setAttribute('aria-label', 'Configure recipe');
		recipeButton.hidden = !card.positioningContext;
		radialMenu.append(foundationsButton, recipeButton, shareButton, deleteButton);
		const openFoundations = () => {
			if (!card.positioningContext) return;
			showAnalysis();
			options.onConfigureOutput(card.positioningContext, card.lane, card.title);
			tutorialEvents.positioningFoundationsConfigurationOpened(
				card.title,
				card.itemId || card.clientId
			);
		};

		let showingFoundations = false;
		const setFoundationsFace = (show: boolean) => {
			showingFoundations = show;
			article.classList.toggle('is-showing-foundations', show);
			foundationsButton.textContent = show ? '👤 Profile' : '👁 Foundations';
			foundationsButton.setAttribute('aria-pressed', String(show));
		};
		foundationsButton.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();
			setFoundationsFace(!showingFoundations);
		}, { signal });
		recipeButton.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();
			openFoundations();
		}, { signal });
		article.addEventListener(VIEW_CARD_FOUNDATIONS_EVENT, openFoundations, { signal });
		article.addEventListener(CONFIGURE_CARD_FOUNDATIONS_EVENT, openFoundations, { signal });
		shareButton.addEventListener(
			'click',
			() => {
				void (async () => {
					if (card.itemId) {
						try {
							await shareLikeToTeam(boardId, card.itemId);
						} catch (error) {
							console.error('Unable to share output with the team', error);
						}
					}
					if (globalThis.neuroCreate.switchToMode) {
						globalThis.neuroCreate.switchToMode('ideate', true, false);
					} else {
						window.location.hash = '#ideate-team';
					}
				})();
			},
			{ signal }
		);
		if (options.isTeam) {
			shareButton.hidden = true;
		}
		deleteButton.addEventListener(
			'click',
			(event) => {
				event.preventDefault();
				event.stopPropagation();
				if (!card.itemId || !card.ownedByCurrentUser || deleteButton.dataset.pending === 'true') return;
				deleteButton.dataset.pending = 'true';
				deleteButton.disabled = true;
				deleteButton.textContent = '🗑 Deleting…';
				void deletePositioningOutput(boardId, card.itemId)
					.then(() => {
						currentCards = currentCards.filter((candidate) => candidate.itemId !== card.itemId);
						lastLikesFingerprint = undefined;
						render();
						updateMetrics();
						window.dispatchEvent(new Event('positioning-output-deleted'));
					})
					.catch((error) => {
						console.error('Unable to delete positioning output', error);
						showNonBlockingMessage('Unable to delete this output. Please try again.');
						deleteButton.textContent = '🗑 Delete output';
						deleteButton.disabled = false;
						delete deleteButton.dataset.pending;
					});
			},
			{ signal }
		);
		inner.append(front, back, radialMenu);
		article.append(inner);
		const shell = createElement('div', 'positioning-canvas-card-shell');
		shell.tabIndex = 0;
		shell.setAttribute('role', 'button');
		shell.setAttribute('aria-label', `${card.title} positioning output`);
		shell.append(article);
		const onCardActivate = () => {
			selectCardShell(shell);
			if (!card.ownedByCurrentUser) {
				showNonBlockingMessage(
					`This output was added by <strong>${escapeHtml(card.ownerName || 'someone else')}</strong>. You cannot edit, delete, or configure this output.`
				);
			}
		};
		shell.addEventListener('positioning-card-select', onCardActivate, { signal });
		shell.addEventListener(
			'keydown',
			(event) => {
				if (event.key !== 'Enter' && event.key !== ' ') return;
				event.preventDefault();
				onCardActivate();
			},
			{ signal }
		);
		shell.addEventListener(
			'focus',
			() => {
				selectCardShell(shell);
			},
			{ signal }
		);
		return shell;
	};

	const createAddCard = (lane: PositioningLane) => {
		const content = laneContent[lane];
		const article = createElement('article', `positioning-add-card positioning-add-card--${lane}`);
		article.tabIndex = 0;
		article.setAttribute('role', 'button');
		article.setAttribute('aria-label', `Generate a new ${content.title} output`);
		article.append(createElement('span', 'positioning-add-card__plus', '+'));
		article.append(createElement('strong', undefined, 'Add output'));
		article.append(createElement('p', undefined, `Generate ${content.title.toLowerCase()}`));
		article.addEventListener('click', () => options.onAddOutput(lane), { signal });
		article.addEventListener(
			'keydown',
			(event) => {
				if (event.key !== 'Enter' && event.key !== ' ') return;
				event.preventDefault();
				options.onAddOutput(lane);
			},
			{ signal }
		);
		return article;
	};

	const createPath = (lane: PositioningLane, fromKey: string, toKey: string, isChild = false) => {
		const path = createSvgElement('path', `positioning-connector positioning-connector--${lane}`);
		if (isChild) path.classList.add('positioning-connector--child');
		path.dataset.lane = lane;
		path.dataset.fromKey = fromKey;
		path.dataset.toKey = toKey;
		return path;
	};

	const render = () => {
		const knownKeys = new Set(positionState.keys());
		mapRoot.replaceChildren();
		nodeObjects.clear();
		const connectors = createSvgElement('g', 'positioning-connectors');
		const nodes = createSvgElement('g', 'positioning-map-nodes');

		(['audience', 'motivation', 'solution', 'brand', 'story'] as PositioningLane[]).forEach(
			(lane) => {
				const hubKey = positioningHubKey(lane);
				connectors.append(createPath(lane, positioningProblemKey, hubKey));
				nodes.append(createBranchNode(lane));
				const laneCards = currentCards.filter((card) => card.lane === lane);

				laneCards.forEach((card, index) => {
					const isPersona = Boolean(card.persona);
					const cardWidth = isPersona ? PERSONA_CARD_WIDTH : CARD_WIDTH;
					const cardHeight = isPersona ? PERSONA_CARD_HEIGHT : CARD_HEIGHT;
					const position = radialCardPosition(lane, index, cardWidth, cardHeight);
					const cardKey = positioningCardKey(card.sourceOutputId);
					connectors.append(createPath(lane, hubKey, cardKey, true));
					nodes.append(
						createForeignObject(
							cardKey,
							{
								x: position.x - CARD_MENU_PAD_X,
								y: position.y - CARD_MENU_PAD_TOP,
							},
							cardWidth + CARD_MENU_PAD_X * 2,
							cardHeight + CARD_MENU_PAD_TOP + CARD_MENU_PAD_BOTTOM,
							createOutputCard(card),
							'positioning-output-object'
						)
					);
				});

				const addPosition = radialCardPosition(lane, laneCards.length);
				const addKey = positioningAddKey(lane);
				connectors.append(createPath(lane, hubKey, addKey, true));
				nodes.append(
					createForeignObject(
						addKey,
						addPosition,
						CARD_WIDTH,
						CARD_HEIGHT,
						createAddCard(lane),
						'positioning-output-object positioning-add-object'
					)
				);
			}
		);

		nodes.append(createProblemNode());
		mapRoot.append(connectors, nodes);
		updateConnectors = () => {
			connectors.querySelectorAll<SVGPathElement>('.positioning-connector').forEach((path) => {
				const from = getNodeCenter(path.dataset.fromKey || '');
				const to = getNodeCenter(path.dataset.toKey || '');
				const lane = path.dataset.lane as PositioningLane | undefined;
				if (!from || !to || !lane) return;
				path.setAttribute('d', connectorPath(lane, from, to));
			});
		};
		const unsavedKeys = new Set(
			Array.from(nodeObjects.keys()).filter((key) => !knownKeys.has(key))
		);
		// Re-run the rectangular-card collision pass for the complete output set.
		// Layout refreshes and collaboration can restore an older overlapping layout;
		// limiting this to newly created nodes left those collisions on the canvas.
		if (currentCards.length || unsavedKeys.size) {
			resolveOutputCollisions();
			updateConnectors();
			persistLayout();
		} else {
			updateConnectors();
		}
	};

	const updateMetrics = () => {
		const insight = workspace.querySelector<HTMLElement>('[data-positioning-insight]');
		const contextMode = workspace.querySelector<HTMLElement>('[data-positioning-context-mode]');
		const context = workspace.querySelector<HTMLElement>('[data-positioning-generation-context]');
		const waitingForOwner = !options.canConfigurePositioning && !options.insight;
		const focusLabel =
			options.insight ||
			(waitingForOwner ? 'Waiting for the board owner' : 'Open positioning exploration');
		if (insight) {
			insight.innerText = focusLabel;
			const description = options.insight ? insightDescriptionFor(options.insight) : '';
			insight.title =
				description ||
				(options.insight
					? `Positioning focus: ${options.insight}`
					: waitingForOwner
						? 'The board owner has not chosen a positioning focus yet'
						: 'No positioning focus was saved');
		}
		const nodeLabels = options.inspireLabels
			.map((label) => label.replace(/\s+/g, ' ').trim())
			.filter(Boolean);
		if (contextMode) {
			contextMode.innerText = ideateContextModeLabel(options.isTeam, nodeLabels.length);
		}
		if (!context) return;
		const vision = summariseText(options.vision || '');
		const pdfSummary = summariseText(options.pdfSummary || '');
		const pdfTopics = (options.pdfTopics || []).map((topic) => topic.trim()).filter(Boolean);
		context.replaceChildren(
			createContextItem('Foundation', ideateContextFoundationMessage(options.isTeam)),
			createContextItem('Project vision', vision || 'No project vision has been added yet.')
		);
		if (pdfSummary) {
			context.append(createContextItem('PDF context', pdfSummary));
		}
		if (pdfTopics.length) {
			context.append(
				createContextChips(
					pdfSummary ? 'PDF topics' : 'PDF context',
					pdfTopics,
					'No PDF context has been added yet.'
				)
			);
		} else if (!pdfSummary) {
			context.append(createContextItem('PDF context', 'No PDF context has been added yet.'));
		}
		context.append(
			createContextChips(
				options.isTeam ? 'Inspire ideas on this board' : 'Your Inspire ideas',
				nodeLabels,
				options.isTeam
					? 'No Inspire ideas on the board yet.'
					: 'You have not added any Inspire ideas yet.'
			)
		);
	};

	let latestRefreshId = 0;
	let activeRefreshCount = 0;
	let lastLikesFingerprint: string | undefined;
	const refresh = async (centerAfterRefresh = false) => {
		const refreshId = ++latestRefreshId;
		activeRefreshCount += 1;
		try {
			const likes = await getUserLikes(boardId, undefined, options.isTeam);
			if (destroyed || refreshId !== latestRefreshId) return;
			const likesFingerprint = JSON.stringify(likes);
			if (likesFingerprint !== lastLikesFingerprint) {
				lastLikesFingerprint = likesFingerprint;
				currentCards = likedItemsToPositioningCards(likes);
				render();
				updateMetrics();
			}
		} catch (error) {
			console.error('Unable to load positioning outputs', error);
			if (destroyed || refreshId !== latestRefreshId) return;
			// Keep the last successfully rendered cards when a background refresh fails.
			// Clearing them here makes a transient request failure look like data loss.
		} finally {
			activeRefreshCount -= 1;
		}
		if (destroyed || refreshId !== latestRefreshId) return;
		if (centerAfterRefresh) setTimeout(options.onCenterMap, 0);
	};

	const revealOverview = () => {
		options.onShowOverview();
		workspace.classList.remove('hidden');
		mapRoot.classList.remove('hidden');
		setSourceVisibility(false);
		analysesPanel?.classList.add('hidden');
		returnButton.classList.add('hidden');
	};

	const showOverview = () => {
		revealOverview();
		void refresh(true);
	};

	document.addEventListener(
		'mousedown',
		(event) => {
			const pill = workspace.querySelector<HTMLDetailsElement>('.positioning-context-pill');
			if (!pill?.open || pill.contains(event.target as Node)) return;
			pill.open = false;
		},
		{ signal }
	);
	returnButton.addEventListener('click', showOverview, { signal });
	window.addEventListener('positioning-output-saved', () => void refresh(), { signal });
	const teamOutputRefreshTimer = options.isTeam
		? window.setInterval(() => {
				if (destroyed || document.hidden || activeRefreshCount > 0) return;
				void refresh();
			}, TEAM_OUTPUT_REFRESH_INTERVAL_MS)
		: undefined;
	if (teamOutputRefreshTimer !== undefined) {
		signal.addEventListener('abort', () => window.clearInterval(teamOutputRefreshTimer), {
			once: true,
		});
		document.addEventListener(
			'visibilitychange',
			() => {
				if (!document.hidden && activeRefreshCount === 0) void refresh();
			},
			{ signal }
		);
	}
	const resolveFlowyOutputCard = (event: Event, cards: HTMLElement[]): HTMLElement | undefined => {
		const target = (
			event as CustomEvent<{
				type?: string;
				id?: string;
				title?: string;
			}>
		).detail;
		if (target?.type === 'positioning-output') {
			const id = target.id?.trim();
			if (id) {
				const idMatch = cards.find(
					(card) =>
						card.dataset.positioningOutputId === id || card.dataset.positioningOutputClientId === id
				);
				if (idMatch) return idMatch;
			}
			const title = target.title?.replace(/\s+/g, ' ').trim().toLocaleLowerCase();
			if (title) {
				const titleMatch = cards.find(
					(card) =>
						card.dataset.positioningOutputTitle?.replace(/\s+/g, ' ').trim().toLocaleLowerCase() ===
						title
				);
				if (titleMatch) return titleMatch;
			}
		}
		return cards.at(-1);
	};
	window.addEventListener(
		'flowy-view-positioning-foundations',
		(event) => {
			void (async () => {
				try {
					revealOverview();
					await refresh();
					const cards = Array.from(
						mapRoot.querySelectorAll<HTMLElement>(
							'.positioning-canvas-card[data-has-positioning-context="true"]'
						)
					);
					const target = resolveFlowyOutputCard(event, cards);
					if (!target) return;
					target.dispatchEvent(new Event(CONFIGURE_CARD_FOUNDATIONS_EVENT));
				} catch (error) {
					console.error('Unable to open positioning foundations', error);
				}
			})();
		},
		{ signal }
	);
	window.addEventListener(
		'flowy-configure-positioning-foundations',
		(event) => {
			void (async () => {
				try {
					revealOverview();
					await refresh();
					const cards = Array.from(
						mapRoot.querySelectorAll<HTMLElement>(
							'.positioning-canvas-card[data-has-positioning-context="true"]'
						)
					);
					const target = resolveFlowyOutputCard(event, cards);
					if (!target) return;
					target.dispatchEvent(new Event(CONFIGURE_CARD_FOUNDATIONS_EVENT));
				} catch (error) {
					console.error('Unable to configure positioning foundations', error);
				}
			})();
		},
		{ signal }
	);
	window.addEventListener('positioning-return-to-map', showOverview, { signal });

	render();
	updateMetrics();
	showOverview();

	return {
		showOverview,
		showAnalysis,
		getProblemCenter: () => getNodeCenter(positioningProblemKey),
		refresh,
		applyLayout,
		destroy: () => {
			destroyed = true;
			abortController.abort();
			mapRoot.remove();
			setSourceVisibility(true);
			workspace.classList.add('hidden');
			returnButton.classList.add('hidden');
		},
	};
};
